import { take, call, put } from 'redux-saga/effects';
import { hashHistory } from 'react-router';
import * as types from '../../lib/actionTypes.es6';
import {
  loginUser,
  getStarted,
  refreshSession,
  recoverPassword,
  logoutUser,
  getUserPartners,
  verifyOtp,
  resendOtp,
} from '../../lib/requests.es6';
import {
  userLoginSuccess,
  userLoginFail,
  sessionRefreshSuccess,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  sessionRefreshFailed,
  getStartedSuccess,
  getStartedFailure,
  mfaChallengeReceived,
  otpVerifySuccess,
  otpVerifyFail,
  otpResendSuccess,
  otpResendFail,
} from './actions.es6';
import { setCurrentPartner } from '../Main/actions.es6';
import { GAloggerException } from '../../lib/monitoring.es6';
import { getPartner, getPartnerGuid, sesssionMonitor } from '../../lib/access.es6';
import { buildFailureMessage, reloadPage } from '../../lib/utils.es6';
import { getFeatureFlagsRequest } from '../FeatureFlags/actions.es6';

// Which partner should be active once authentication completes.
//
// This ran as `user_partners[0]` unconditionally, which meant every
// re-authentication — including the silent token refresh behind a "Session
// Expired" prompt — threw away the partner the admin had selected and snapped
// the app back to whichever partner the API happened to list first. An admin
// could check the header, start a form against one insurer, and have the tenant
// change underneath them before they pressed Save. Every mutating request sends
// the active partner as a query parameter, so that is a data-integrity problem,
// not a cosmetic one.
//
// The previous selection is honoured whenever it is still one the user is
// authorised for; otherwise (first login, or a different user on this browser)
// it falls back to the first partner as before.
export function resolveActivePartner(userPartners) {
  const partners = userPartners || [];
  if (!partners.length) return null;

  let stored = null;
  try {
    stored = getPartner();
  } catch (e) {
    stored = null;
  }
  if (!stored || !stored.guid) return partners[0];

  let match = null;
  partners.forEach(function(candidate) {
    if (match === null && candidate && candidate.guid === stored.guid) {
      match = candidate;
    }
  });

  return match || partners[0];
}

function getLoginFailureMessage(defaultMessage, errorPayload) {
  if (!errorPayload) {
    return defaultMessage;
  }

  if (errorPayload.message) {
    return errorPayload.message;
  }

  if (errorPayload.error_description) {
    return errorPayload.error_description;
  }

  return buildFailureMessage(defaultMessage, errorPayload);
}

/**
 * this saga continually watches login request actions
 */
export function* loginRequestWatcher() {
  while (true) {
    // pause and wait for the USER_LOGIN_REQUEST action to be dispatched
    const regActionResponse = yield take(types.LOGIN_REQUESTED);

    try {
      const authResponse = yield call(loginUser, regActionResponse.payload);
      const responseData = authResponse.data;

      console.log('[MFA DEBUG] login response status:', responseData.status);

      // MFA challenge — proxy returns status without Keycloak tokens
      if (responseData.status === 'MFA_REQUIRED') {
        console.log('[MFA DEBUG] MFA_REQUIRED — dispatching mfaChallengeReceived');
        yield put(mfaChallengeReceived(responseData));
      } else if (responseData.status === 'PASSWORD_EXPIRED') {
        yield put(
          userLoginFail(
            getLoginFailureMessage(
              'Your password has expired. Please reset your password.',
              responseData,
            ),
          ),
        );
      } else {
        // Normal success — access_token is present
        saveAuthData(responseData);
        const userInfo = getUserInfo(responseData.access_token);
        const userPartners = yield call(getUserPartners, userInfo.partners);

        const userProfileAuthData = aggregateUserprofileDataFromLogin(
          responseData, userInfo, userPartners.data
        );

        yield put(userLoginSuccess(userProfileAuthData));
        yield put(
          setCurrentPartner(
            resolveActivePartner(userProfileAuthData.user_partners)
          )
        );
        yield put(getFeatureFlagsRequest());
      }
    } catch (e) {
      console.log('[MFA DEBUG] login error:', e);
      GAloggerException(e);
      yield put(
        userLoginFail(
          getLoginFailureMessage(
            'There may be a system issue. Please try again later or contact your administrator.',
            e && e.response ? e.response.data : null,
          ),
        ),
      );
    }
  }
}

export function* otpVerifyRequestWatcher() {
  while (true) {
    const action = yield take(types.MFA_OTP_VERIFY_REQUEST);
    try {
      const response = yield call(
        verifyOtp,
        action.payload.pendingAuthToken,
        action.payload.otp
      );
      const responseData = response.data;
      saveAuthData(responseData);
      const userInfo = getUserInfo(responseData.access_token);
      const userPartners = yield call(getUserPartners, userInfo.partners);
      const userProfileAuthData = aggregateUserprofileDataFromLogin(
        responseData, userInfo, userPartners.data
      );
      yield put(otpVerifySuccess(userProfileAuthData));
      yield put(
        setCurrentPartner(
          resolveActivePartner(userProfileAuthData.user_partners)
        )
      );
      yield put(getFeatureFlagsRequest());
    } catch (e) {
      GAloggerException(e);
      var message = 'Invalid or expired code. Please try again.';
      if (e.response && e.response.data && e.response.data.message) {
        message = e.response.data.message;
      }
      yield put(otpVerifyFail(message));
    }
  }
}

export function* otpResendRequestWatcher() {
  while (true) {
    const action = yield take(types.MFA_OTP_RESEND_REQUEST);
    try {
      yield call(resendOtp, action.payload.pendingAuthToken);
      yield put(otpResendSuccess());
    } catch (e) {
      GAloggerException(e);
      var message = 'Unable to resend code. Please try again.';
      if (e.response && e.response.data && e.response.data.message) {
        message = e.response.data.message;
      }
      yield put(otpResendFail(message));
    }
  }
}

export function* refreshSessionRequestWatcher() {
  while (yield take('SESSION_REFRESH_REQUEST')) {
    try {
      const response = yield call(refreshSession);
      const profileData = aggregateUserprofileDataFromRefresh(response.data)

      yield put(sessionRefreshSuccess(profileData));
      sesssionMonitor();
    } catch (e) {
      GAloggerException(e, 'Session refresh failed');
      yield put(userLoginFail('Refreshing the session fail. Please login'));
      yield put(sessionRefreshFailed());
    }
  }
}

export function* forgotPasswordRequestWatcher() {
  while (true) {
    const response = yield take(types.FORGOT_PASSWORD_REQUEST);
    try {
      yield call(recoverPassword, response.payload);
      yield put(forgotPasswordSuccess());
    } catch (e) {
      GAloggerException(e);
      yield put(forgotPasswordFailure());
    }
  }
}

export function* logoutRequestWatcher() {
  while (true) {
    yield take(types.USER_LOGOUT);
    try {
     const response =  yield call(logoutUser);
     console.log(response.data)
    } catch (e) {
      GAloggerException(e);
    } finally {
      logout();
    }
  }
}

export function* getStartedMessageRequestWatcher() {
  while (true) {
    const request = yield take(types.GET_STARTED_REQUEST);
    try {
      const response =  yield call(getStarted, request.payload);
      yield put(getStartedSuccess(response.data));
    } catch (e) {
      GAloggerException(e);
      console.log("get started error",e)
      yield put(getStartedFailure(e.response.data));
    }
  }
}


function logout() {
  const partnerGuid = getPartnerGuid();
  if (partnerGuid === 'britamguid') {
    hashHistory.push('britam');
  } else {
    hashHistory.push('login');
  }

  const maxId = setTimeout(() => { }, 0);
  for (let i = 0; i < maxId; i += 1) {
    clearTimeout(i);
  }
  
  localStorage.clear();
  reloadPage()
}

function saveAuthData(authInfo) {
  authInfo.timestamp = Date.now();
  authInfo.expires_in = authInfo.expires_in * 1000 + authInfo.timestamp;
  localStorage.setItem('auth', JSON.stringify(authInfo));
}

function aggregateUserprofileDataFromLogin(authInfo, userInfo, userPartners) {
  const profile = authInfo;
  profile.user_guid = userInfo.sub;
  profile.user_fullname = userInfo.name;
  profile.user_partners = userPartners;
  profile.branch = userInfo.branch;

  profile.user_principle = {
    authorities: transformUserRolesToAuthorities(userInfo.realm_access.roles),
    username: userInfo.preferred_username,
    email: userInfo.email,
    email_verified: userInfo.email_verified
  };

  localStorage.setItem('auth', JSON.stringify(profile));
  localStorage.setItem(
    'partner',
    JSON.stringify(userPartners[0])
  );

  return profile;
}


function transformUserRolesToAuthorities(roles) {
  const authorities = []
  for (let i = 0; i < roles.length; i++) {
    authorities.push({ authority: roles[i] })
  }

  return authorities;
}

function aggregateUserprofileDataFromRefresh(authData) {
  authData.timestamp = Date.now();
  authData.expires_in =
    authData.expires_in * 1000 + authData.timestamp;

  const profileData = JSON.parse(localStorage.getItem('auth'));
  profileData.timestamp = authData.timestamp;
  profileData.expires_in = authData.expires_in;
  profileData.access_token = authData.access_token;
  profileData.refresh_expires_in = authData.refresh_expires_in;
  profileData.refresh_token = authData.refresh_token;


  localStorage.setItem('auth', JSON.stringify(profileData));

  return profileData;
}


function getUserInfo(accessToken) {
  return JSON.parse(atob(accessToken.split('.')[1]));
}
